import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch } from '../../api/apiClient';
import { formatTemperature, normalizeDailyWeatherRecord } from '../../util/temperature';
import { useTemperatureUnit } from '../../util/useTemperatureUnit';
import { createPortfolioDateFormatter, DEFAULT_TIME_ZONE } from '../../utils/dateTime';

const buildWeatherDateFormatters = (timeZone) => {
  const formatter = createPortfolioDateFormatter(timeZone);
  return {
    formatMonth: (date) => formatter.formatDate(date, { month: 'short' }),
    formatMonthLabel: (date, chartAngle) => {
      const month = formatter.formatDate(date, { month: 'short' });
      if (chartAngle <= 0) {
        return month;
      }
      const yearSuffix = `'${String(formatter.getDateParts(date)?.year ?? '').slice(-2)}`;
      return `${month} ${yearSuffix}`;
    },
    formatFullDate: (date) => formatter.formatDate(date, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }),
  };
};

const WeatherRadarChart = ({ portfolioName = '', previousDays = 365, timeZone = DEFAULT_TIME_ZONE }) => {
  const temperatureUnit = useTemperatureUnit();
  const size = 600;
  const radius = size / 2;
  const margin = 45;
  const chartRadius = radius - margin;
  const innerRadius = chartRadius * 0.25;

  const svgRef = useRef(null);
  const [activeData, setActiveData] = useState(null);
  const [activeAngle, setActiveAngle] = useState(null);
  const [dailyRows, setDailyRows] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!portfolioName) {
        setDailyRows([]);
        setLoadError(null);
        return;
      }

      setLoadError(null);

      try {
        const payload = await apiFetch(`/api/weather/portfolio/${portfolioName}/historical/daily/${previousDays}`);
        if (cancelled) {
          return;
        }
        const daily = Array.isArray(payload?.daily) ? payload.daily : [];
        setDailyRows(daily.map(normalizeDailyWeatherRecord).filter(Boolean));
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load historical weather:', err);
          setDailyRows([]);
          setLoadError(err);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [portfolioName, previousDays]);

  useEffect(() => {
    setActiveData(null);
    setActiveAngle(null);
  }, [dailyRows]);

  const { data, startMs, endMs, monthTicks } = useMemo(() => {
    const withDates = [...dailyRows].sort((a, b) => a.parsedDate - b.parsedDate);

    if (withDates.length === 0) {
      return { data: [], startMs: 0, endMs: 1, monthTicks: [] };
    }

    let s = withDates[0].parsedDate.getTime();
    let e = withDates[withDates.length - 1].parsedDate.getTime();
    if (e <= s) {
      e = s + 86400000;
    }

    const ticks = [];
    const cursor = new Date(withDates[0].parsedDate);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    if (cursor > withDates[0].parsedDate) {
      cursor.setMonth(cursor.getMonth() - 1);
    }
    const last = withDates[withDates.length - 1].parsedDate;
    while (cursor <= last) {
      if (cursor >= withDates[0].parsedDate) {
        ticks.push(new Date(cursor));
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }

    if (ticks.length === 0) {
      const d0 = withDates[0].parsedDate;
      ticks.push(new Date(d0.getFullYear(), d0.getMonth(), 1));
    }

    return { data: withDates, startMs: s, endMs: e, monthTicks: ticks };
  }, [dailyRows]);

  const angleScale = useCallback(
    (date) => {
      const span = endMs - startMs;
      return ((date.getTime() - startMs) / span) * Math.PI * 2;
    },
    [startMs, endMs]
  );

  const invertAngle = useCallback(
    (angle) => {
      const span = endMs - startMs;
      const t = startMs + (angle / (Math.PI * 2)) * span;
      return new Date(t);
    },
    [startMs, endMs]
  );

  const radiusScale = (temp) => {
    const minTemp = -10;
    const maxTemp = 110;
    const minR = innerRadius + 30;
    const maxR = chartRadius;
    return minR + ((temp - minTemp) / (maxTemp - minTemp)) * (maxR - minR);
  };

  const cloudCoverScale = (pct) => (pct / 100) * 6;
  const precipScale = (pct) => (pct / 100) * 5;

  const handlePointerMove = (e) => {
    if (!svgRef.current || data.length === 0) return;

    const rect = svgRef.current.getBoundingClientRect();
    const touchX = (e.clientX || e.touches?.[0]?.clientX) - rect.left - radius;
    const touchY = (e.clientY || e.touches?.[0]?.clientY) - rect.top - radius;

    let angle = Math.atan2(touchY, touchX);
    angle = angle + Math.PI / 2;
    if (angle < 0) angle += Math.PI * 2;

    const activeDate = invertAngle(angle);
    const closestDay = data.reduce((prev, curr) =>
      Math.abs(curr.parsedDate - activeDate) < Math.abs(prev.parsedDate - activeDate) ? curr : prev
    );

    setActiveData(closestDay);
    setActiveAngle(angle);
  };

  const handlePointerLeave = () => {
    setActiveData(null);
    setActiveAngle(null);
  };

  const temperatureTicks = useMemo(() => {
    const ticks = [];
    for (let t = -10; t <= 110; t += 20) {
      ticks.push(t);
    }
    return ticks;
  }, []);

  const { formatMonthLabel, formatFullDate } = useMemo(
    () => buildWeatherDateFormatters(timeZone),
    [timeZone],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'sans-serif' }}>
      <h2>Historical Weather</h2>
      {loadError ? (
        <p style={{ color: '#B45309', marginBottom: '8px', fontSize: '14px' }}>
          Could not load historical weather for this portfolio.
        </p>
      ) : null}
      <svg
        ref={svgRef}
        width={size}
        height={size}
        onMouseMove={handlePointerMove}
        onTouchMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
        onTouchEnd={handlePointerLeave}
        style={{ cursor: 'crosshair', touchAction: 'none' }}
      >
        <g transform={`translate(${radius}, ${radius})`}>
          {monthTicks.map((month, i) => {
            const angle = angleScale(month) - Math.PI / 2;
            return (
              <line
                key={`month-line-${month.getTime()}-${i}`}
                x1={Math.cos(angle) * innerRadius}
                y1={Math.sin(angle) * innerRadius}
                x2={Math.cos(angle) * chartRadius}
                y2={Math.sin(angle) * chartRadius}
                stroke="#E2E8F0"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            );
          })}

          {data.map((d, i) => {
            if (d.cloudCoverPct < 1) return null;
            const angle = angleScale(d.parsedDate) - Math.PI / 2;
            const r = innerRadius + 10;
            return (
              <circle
                key={`cloud-${d.date}-${i}`}
                cx={Math.cos(angle) * r}
                cy={Math.sin(angle) * r}
                r={cloudCoverScale(d.cloudCoverPct)}
                fill="#213b5f"
                opacity="0.4"
              />
            );
          })}

          {data.map((d, i) => {
            const angle = angleScale(d.parsedDate) - Math.PI / 2;
            const minRadius = radiusScale(d.minTemp);
            const maxRadius = radiusScale(d.maxTemp);
            return (
              <line
                key={`temp-${d.date}-${i}`}
                x1={Math.cos(angle) * minRadius}
                y1={Math.sin(angle) * minRadius}
                x2={Math.cos(angle) * maxRadius}
                y2={Math.sin(angle) * maxRadius}
                stroke="#FF8C00"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.85"
              />
            );
          })}

          {data.map((d, i) => {
            if (d.precipitationPct < 10) return null;
            const angle = angleScale(d.parsedDate) - Math.PI / 2;
            const r = radiusScale(d.maxTemp) + 12;
            return (
              <circle
                key={`precip-${d.date}-${i}`}
                cx={Math.cos(angle) * r}
                cy={Math.sin(angle) * r}
                r={precipScale(d.precipitationPct)}
                fill="#005ac9"
                opacity="0.7"
              />
            );
          })}

          {data.map((d, i) => {
            if (d.uvMax < 8) return null;
            const angle = angleScale(d.parsedDate) - Math.PI / 2;
            const r = chartRadius + 6;
            return (
              <circle
                key={`uv-${d.date}-${i}`}
                cx={Math.cos(angle) * r}
                cy={Math.sin(angle) * r}
                r={2.5}
                fill="#EF4444"
              />
            );
          })}

          {monthTicks.map((month, i) => {
            const chartAngle = angleScale(month);
            const angle = chartAngle - Math.PI / 2;
            const textRadius = chartRadius + 22;
            return (
              <text
                key={`month-label-${month.getTime()}-${i}`}
                x={Math.cos(angle) * textRadius}
                y={Math.sin(angle) * textRadius}
                fill="#64748B"
                fontSize="13"
                fontWeight="bold"
                textAnchor="middle"
                alignmentBaseline="middle"
              >
                {formatMonthLabel(month, chartAngle)}
              </text>
            );
          })}

          {temperatureTicks.map((tick) => {
            const r = radiusScale(tick);
            return (
              <g key={`tick-${tick}`}>
                <circle
                  r={r}
                  stroke={tick === 32 ? '#3B82F6' : '#d8e2f0'}
                  strokeWidth={tick === 32 ? '1.5' : '1'}
                  fill="none"
                  strokeDasharray={tick === 32 ? '4 4' : undefined}
                />
                <text
                  y={-r - 4}
                  x={4}
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="3"
                  strokeLinejoin="round"
                  fontSize="12"
                  fontWeight="bold"
                >
                  {formatTemperature(tick, temperatureUnit)}
                </text>
                <text
                  y={-r - 4}
                  x={4}
                  fill={tick === 32 ? '#2563EB' : '#334155'}
                  fontSize="12"
                  fontWeight="bold"
                >
                  {formatTemperature(tick, temperatureUnit)}
                </text>
              </g>
            );
          })}

          <circle r={innerRadius} fill="#FFFFFF" />

          {activeData && (
            <g>
              <line
                x1={0}
                y1={0}
                x2={Math.cos(activeAngle - Math.PI / 2) * chartRadius}
                y2={Math.sin(activeAngle - Math.PI / 2) * chartRadius}
                stroke="#334155"
                strokeWidth="1.5"
                strokeDasharray="4 2"
                opacity="0.6"
              />
              <circle
                cx={Math.cos(activeAngle - Math.PI / 2) * radiusScale(activeData.minTemp)}
                cy={Math.sin(activeAngle - Math.PI / 2) * radiusScale(activeData.minTemp)}
                r={4}
                fill="#FFFFFF"
                stroke="#334155"
                strokeWidth="2"
              />
              <circle
                cx={Math.cos(activeAngle - Math.PI / 2) * radiusScale(activeData.maxTemp)}
                cy={Math.sin(activeAngle - Math.PI / 2) * radiusScale(activeData.maxTemp)}
                r={4}
                fill="#FFFFFF"
                stroke="#334155"
                strokeWidth="2"
              />
            </g>
          )}
        </g>
      </svg>

      {activeData ? (
        <div
          style={{
            marginTop: '0px',
            padding: '15px',
            backgroundColor: '#F8FAFC',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            width: '320px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0F172A' }}>
            {formatFullDate(activeData.parsedDate)}
          </h3>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#FF8C00' }}>
            <strong>Temp:</strong> {formatTemperature(activeData.minTemp, temperatureUnit)} – {formatTemperature(activeData.maxTemp, temperatureUnit)}
          </p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#005ac9' }}>
            <strong>Precipitation:</strong> {Math.round(activeData.precipitationPct)}% ({activeData.precipitationSum.toFixed(2)} mm)
          </p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#213b5f' }}>
            <strong>Cloud Cover:</strong> {Math.round(activeData.cloudCoverPct)}%
          </p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#EF4444' }}>
            <strong>UV Index:</strong> {activeData.uvMax.toFixed(1)}
          </p>
        </div>
      ) : (
        <div
          style={{
            marginTop: '0px',
            padding: '15px',
            backgroundColor: '#F8FAFC',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            width: '320px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0F172A' }}>
            Chart Legend
          </h3>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#FF8C00' }}>
            <strong>Temperature</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#005ac9' }}>
            <strong>Precipitation</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#213b5f' }}>
            <strong>Cloud Cover</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#EF4444' }}>
            <strong>UV Index</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default WeatherRadarChart;
